import React, { useEffect, useState, useCallback } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

/* ---------------- INFO CARD ---------------- */
function InfoCard ({ title, value, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full rounded-xl p-4 text-left shadow-sm transition sm:p-5 sm:py-6
        ${active ? "border-2 border-[#7A5AF8] bg-[#7A5AF8] bg-opacity-20" : "border-2 border-white bg-white hover:bg-gray-50"}
      `}
        >
            <p className="mb-1 text-sm font-semibold text-black sm:mb-2 sm:text-base">{title}</p>
            <p className="aoboshi-one break-all text-lg font-bold text-black sm:text-2xl">
                {typeof value === "number" ? value.toLocaleString() : value}
            </p>
        </button>
    );
}

/* ---------------- DASHBOARD ---------------- */
const Dashboard = () => {
    const [hoveredBar, setHoveredBar] = useState(null);
  const [hoveredValue, setHoveredValue] = useState(null);

    // Memoized custom bar shape for hover effect
    const renderBarShape = useCallback((props) => {
      const { x, y, width, height, index, value } = props;
      const isHovered = hoveredBar === index;
      return (
        <g>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={6}
            fill={isHovered ? "#5B4DF7" : "#6D5EF7"}
            opacity={isHovered ? 1 : 0.25}
            style={{ transition: 'all 0.2s' }}
            onMouseEnter={() => {
              setHoveredBar(index);
              setHoveredValue(value);
            }}
            onMouseLeave={() => {
              setHoveredBar(null);
              setHoveredValue(null);
            }}
          />
        </g>
      );
    }, [hoveredBar]);
  // ❗ null = no card selected (overview mode)
  const [activeCard, setActiveCard] = useState(null);
  const [activeFilter, setActiveFilter] = useState("Week");
  const [open, setOpen] = useState(false);

  // auth cookie helper
  const getCookie = (name) => {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)")
    );
    return match ? decodeURIComponent(match[1]) : null;
  };

  // API series state
  const [labels, setLabels] = useState([]);
  const [freeSeries, setFreeSeries] = useState([]);
  const [paidSeries, setPaidSeries] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [freeUsers, setFreeUsers] = useState(0);
  const [paidUsers, setPaidUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Build chart data from API series
  const buildOverviewData = () => {
    const out = [];
    for (let i = 0; i < labels.length; i++) {
      const free = Number(freeSeries[i] ?? 0);
      const paid = Number(paidSeries[i] ?? 0);
      out.push({
        name: labels[i] ?? String(i + 1),
        total: free + paid,
        register: free,
        login: null,
        paid: paid,
      });
    }
    return out;
  };

  const buildSingleMetricData = (metric) => {
    const arr = [];
    for (let i = 0; i < labels.length; i++) {
      let value = 0;
      if (metric === "total") value = Number(freeSeries[i] ?? 0) + Number(paidSeries[i] ?? 0);
      else if (metric === "register" || metric === "login") value = Number(freeSeries[i] ?? 0);
      else if (metric === "paid") value = Number(paidSeries[i] ?? 0);
      arr.push({ name: labels[i] ?? String(i + 1), value });
    }
    return arr;
  };

  const filters = ["Day", "Week", "Month", "Quarter", "Year"];

  // Fetch summary when filter changes
  useEffect(() => {
    const fetchSummary = async () => {
      const token = getCookie("admin_token");
      if (!token) {
        setError("Not authenticated");
        setLabels([]);
        setFreeSeries([]);
        setPaidSeries([]);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const body = new URLSearchParams();
        body.append("range", activeFilter.toLowerCase());
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/report/summary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
          body: body.toString(),
        });
        if (!res.ok) throw new Error("Failed to load summary");
        const json = await res.json();
        const d = json?.data?.[0];
        const series = d?.series || {};
        setLabels(series.labels || []);
        setFreeSeries(series.free_users || []);
        setPaidSeries(series.paid_users || []);
        setTotalUsers(d?.total_users || 0);
        setFreeUsers(d?.free_users || 0);
        setPaidUsers(d?.paid_users || 0);
      } catch (e) {
        setError(e.message || "Failed to load summary");
        setLabels([]);
        setFreeSeries([]);
        setPaidSeries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain bg-[#F5F4FF] p-3 pb-6 sm:gap-6 sm:p-4 md:p-6 touch-pan-y [-webkit-overflow-scrolling:touch]">
      <h1 className="shrink-0 text-xl font-bold sm:text-2xl">Users Report</h1>

      {error && (
        <p className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {/* ---------- CARDS ---------- */}
      <div className="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-6">
        <InfoCard
          title="Total User"
          value={totalUsers}
          active={activeCard === "total"}
          onClick={() => setActiveCard(activeCard === "total" ? null : "total")}
        />
        <InfoCard
          title="Register User"
          value={freeUsers}
          active={activeCard === "register"}
          onClick={() => setActiveCard(activeCard === "register" ? null : "register")}
        />
        <InfoCard
          title="Login User"
          value={freeUsers}
          active={activeCard === "login"}
          onClick={() => setActiveCard(activeCard === "login" ? null : "login")}
        />
        <InfoCard
          title="Paid User"
          value={paidUsers}
          active={activeCard === "paid"}
          onClick={() => setActiveCard(activeCard === "paid" ? null : "paid")}
        />
      </div>

      {/* ---------- CHART CONTAINER ---------- */}
      <div className="min-w-0 flex-1 rounded-2xl bg-white p-3 shadow-lg sm:p-4 md:p-6">
        {/* HEADER */}
        <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4">
          <p className="shrink-0 text-sm font-medium text-gray-500 sm:text-base">
            {activeCard ? "Total Users in" : "Users in"}
          </p>

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm sm:px-4"
            >
              {activeFilter}
              <img src="./chatbar/dark-arrow.svg" alt="" className={`h-4 w-4 opacity-60 ${open ? "-rotate-90" : "rotate-90"}`} />
            </button>

            {open && (
              <div className="absolute left-0 z-20 mt-2 w-32 rounded-xl bg-white shadow-lg">
                {filters.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => {
                      setActiveFilter(f);
                      setOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}
          </div>
          </div>
        {activeCard === null && (
          <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#1E3A8A" }} />
              <span className="text-xs text-gray-600 sm:text-sm">Total</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#4F46E5" }} />
              <span className="text-xs text-gray-600 sm:text-sm">Register</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#93C5FD" }} />
              <span className="text-xs text-gray-600 sm:text-sm">Login</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#C7D2FE" }} />
              <span className="text-xs text-gray-600 sm:text-sm">Paid</span>
            </div>
          </div>
        )}
        </div>

        {loading && (
          <p className="mb-3 text-center text-sm text-gray-500">Loading report…</p>
        )}

        {/* ---------- CHART ---------- */}
        <div className="h-[240px] min-w-0 w-full sm:h-[320px] md:h-[420px]">
          <ResponsiveContainer width="100%" height="100%">
            {activeCard === null ? (
              <LineChart data={buildOverviewData()} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={8}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "white",
                    fontSize: "12px",
                  }}
                />

                <Line dataKey="total" stroke="#1E3A8A" strokeWidth={2} dot={false} />
                <Line dataKey="register" stroke="#4F46E5" strokeWidth={2} dot={false} />
                <Line dataKey="login" stroke="#93C5FD" strokeWidth={2} dot={false} />
                <Line dataKey="paid" stroke="#C7D2FE" strokeWidth={2} dot={false} />
              </LineChart>
            ) : (
              <BarChart data={buildSingleMetricData(activeCard)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={6}
                  tickFormatter={(label) => {
                    if (typeof label === "string" && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
                      return label.split("-")[2];
                    }
                    if (typeof label === "string" && label.length > 6) {
                      return label.slice(0, 5);
                    }
                    return label;
                  }}
                />
                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} width={40} axisLine={false} tickLine={false} />
                <Tooltip cursor={false} />
          {hoveredBar !== null && hoveredValue !== null && (
            <ReferenceLine
              y={hoveredValue}
              stroke="#6D5EF7"
              strokeDasharray="4 4"
              strokeWidth={1}
            />
          )}
                <Bar
                  dataKey="value"
                  radius={[6, 6, 0, 0]}
                  barSize={activeFilter === "Day" ? 24 : 20}
                  maxBarSize={28}
                  shape={renderBarShape}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;





// import React, { useEffect, useState, useCallback } from "react";
// import {
//   LineChart,
//   Line,
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
//   ReferenceLine,
// } from "recharts";

// /* ---------------- INFO CARD ---------------- */
// function InfoCard ({ title, value, active, onClick }) {
//     return (
//         <div
//             className={`rounded-xl p-5 py-8 shadow-sm text-left transition cursor-pointer
//         ${active ? "bg-[#7A5AF8] border-2 border-[#7A5AF8] bg-opacity-20" : "bg-white hover:bg-gray-50 border-2 border-white"}
//       `}
//             onClick={onClick}
//         >
//             <p className="text-black text-sm mb-2 leading-[38px]">{title}</p>
//             <p className="font-bold text-black aoboshi-one text-2xl">{value}</p>
//         </div>
//     );
// }

// /* ---------------- DASHBOARD ---------------- */
// const Dashboard = () => {
//     const [hoveredBar, setHoveredBar] = useState(null);
//   const [hoveredValue, setHoveredValue] = useState(null);
//     const [hoveredBarRect, setHoveredBarRect] = useState(null);

//     // Memoized custom bar shape for hover effect
//     const renderBarShape = useCallback((props) => {
//       const { x, y, width, height, index, value } = props;
//       const isHovered = hoveredBar === index;
//       return (
//         <g>
//           <rect
//             x={x}
//             y={y}
//             width={width}
//             height={height}
//             rx={6}
//             fill={isHovered ? "#5B4DF7" : "#6D5EF7"}
//             opacity={isHovered ? 1 : 0.25}
//             style={{ transition: 'all 0.2s' }}
//            onMouseEnter={() => {
//                         setHoveredBar(index);
//                         setHoveredBarRect({ x, y, width });
//                     }}
//                     onMouseLeave={() => {
//                         setHoveredBar(null);
//                         setHoveredBarRect(null);
//                     }}
//           />
//         </g>
//       );
//     }, [hoveredBar]);
//   // ❗ null = no card selected (overview mode)
//   const [activeCard, setActiveCard] = useState(null);
//   const [activeFilter, setActiveFilter] = useState("Week");
//   const [open, setOpen] = useState(false);

//   // auth cookie helper
//   const getCookie = (name) => {
//     const match = document.cookie.match(
//       new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)")
//     );
//     return match ? decodeURIComponent(match[1]) : null;
//   };

//   // API series state
//   const [labels, setLabels] = useState([]);
//   const [freeSeries, setFreeSeries] = useState([]);
//   const [paidSeries, setPaidSeries] = useState([]);
//   const [totalUsers, setTotalUsers] = useState(0);
//   const [freeUsers, setFreeUsers] = useState(0);
//   const [paidUsers, setPaidUsers] = useState(0);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   // Build chart data from API series
//   const buildOverviewData = () => {
//     const out = [];
//     for (let i = 0; i < labels.length; i++) {
//       const free = Number(freeSeries[i] ?? 0);
//       const paid = Number(paidSeries[i] ?? 0);
//       out.push({
//         name: labels[i] ?? String(i + 1),
//         total: free + paid,
//         register: free,
//         login: null,
//         paid: paid,
//       });
//     }
//     return out;
//   };

//   const buildSingleMetricData = (metric) => {
//     const arr = [];
//     for (let i = 0; i < labels.length; i++) {
//       let value = 0;
//       if (metric === "total") value = Number(freeSeries[i] ?? 0) + Number(paidSeries[i] ?? 0);
//       else if (metric === "register" || metric === "login") value = Number(freeSeries[i] ?? 0);
//       else if (metric === "paid") value = Number(paidSeries[i] ?? 0);
//       arr.push({ name: labels[i] ?? String(i + 1), value });
//     }
//     return arr;
//   };

//   const filters = ["Day", "Week", "Month", "Quarter", "Year"];

//   // Fetch summary when filter changes
//   useEffect(() => {
//     const fetchSummary = async () => {
//       const token = getCookie("admin_token");
//       if (!token) {
//         setError("Not authenticated");
//         setLabels([]);
//         setFreeSeries([]);
//         setPaidSeries([]);
//         return;
//       }
//       setLoading(true);
//       setError("");
//       try {
//         const body = new URLSearchParams();
//         body.append("range", activeFilter.toLowerCase());
//         const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/report/summary`, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/x-www-form-urlencoded",
//             Authorization: `Bearer ${token}`,
//           },
//           body: body.toString(),
//         });
//         if (!res.ok) throw new Error("Failed to load summary");
//         const json = await res.json();
//         const d = json?.data?.[0];
//         const series = d?.series || {};
//         setLabels(series.labels || []);
//         setFreeSeries(series.free_users || []);
//         setPaidSeries(series.paid_users || []);
//         setTotalUsers(d?.total_users || 0);
//         setFreeUsers(d?.free_users || 0);
//         setPaidUsers(d?.paid_users || 0);
//       } catch (e) {
//         setError(e.message || "Failed to load summary");
//         setLabels([]);
//         setFreeSeries([]);
//         setPaidSeries([]);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchSummary();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [activeFilter]);

//   return (
//     <div className="min-h-screen bg-[#F5F4FF] p-6 space-y-6">
//       <h1 className="text-2xl font-bold">Users Report</h1>

//       {/* ---------- CARDS ---------- */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
//         <InfoCard
//           title="Total User"
//           value={totalUsers}
//           active={activeCard === "total"}
//           onClick={() => setActiveCard(activeCard === "total" ? null : "total")}
//         />
//         <InfoCard
//           title="Register User"
//           value={freeUsers}
//           active={activeCard === "register"}
//           onClick={() => setActiveCard(activeCard === "register" ? null : "register")}
//         />
//         <InfoCard
//           title="Login User"
//           value={freeUsers}
//           active={activeCard === "login"}
//           onClick={() => setActiveCard(activeCard === "login" ? null : "login")}
//         />
//         <InfoCard
//           title="Paid User"
//           value={paidUsers}
//           active={activeCard === "paid"}
//           onClick={() => setActiveCard(activeCard === "paid" ? null : "paid")}
//         />
//       </div>

//       {/* ---------- CHART CONTAINER ---------- */}
//       <div className="bg-white rounded-2xl p-6 shadow-lg">
//         {/* HEADER */}
//         <div className="flex items-center justify-between gap-6 mb-4">
//             <div className="flex items-center gap-10">
//           <p className="text-gray-500 font-medium">
//             {activeCard ? "Total Users in" : "Users in"}
//           </p>

//           {/* ✅ WHITE DROPDOWN */}
//           <div className="relative">
//             <button
//               onClick={() => setOpen(!open)}
//               className="bg-white border rounded-lg px-4 py-2 flex items-center gap-2"
//             >
//               {activeFilter}
//         <img src="./chatbar/dark-arrow.svg" className={`w-4 h-4 opacity-60 ${open ? '-rotate-90' : 'rotate-90'}`} />
//             </button>

//             {open && (
//               <div className="absolute mt-2 bg-white rounded-xl shadow w-32 z-10">
//                 {filters.map((f) => (
//                   <button
//                     key={f}
//                     onClick={() => {
//                       setActiveFilter(f);
//                       setOpen(false);
//                     }}
//                     className="w-full text-left px-4 py-2 hover:bg-gray-100"
//                   >
//                     {f}
//                   </button>
//                 ))}
//               </div>
//             )}
//           </div>
//           </div>
//             {/* LEGEND */}
//         {activeCard === null && (
//           <div className="flex gap-6 flex-wrap">
//             <div className="flex items-center gap-2">
//               <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#1E3A8A' }}></div>
//               <span className="text-sm text-gray-600">Total Users</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#4F46E5' }}></div>
//               <span className="text-sm text-gray-600">Register Users</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#93C5FD' }}></div>
//               <span className="text-sm text-gray-600">Login Users</span>
//             </div>
//             <div className="flex items-center gap-2">
//               <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#C7D2FE' }}></div>
//               <span className="text-sm text-gray-600">Paid Users</span>
//             </div>
//           </div>
//         )}
//         </div>

      

//         {/* ---------- CHART ---------- */}
//         <div className="h-[420px]">
//           <ResponsiveContainer width="100%" height="100%">
//             {activeCard === null ? (
//               /* 🔥 OVERVIEW MULTI-LINE GRAPH */
//               <LineChart data={buildOverviewData()}>
//                 <CartesianGrid strokeDasharray="3 3" vertical={false} />
//                 <XAxis dataKey="name" />
//                 <YAxis />
//                 <Tooltip />

//                 <Line dataKey="total" stroke="#1E3A8A" strokeWidth={2} />
//                 <Line dataKey="register" stroke="#4F46E5" strokeWidth={2} />
//                 <Line dataKey="login" stroke="#93C5FD" strokeWidth={2} />
//                 <Line dataKey="paid" stroke="#C7D2FE" strokeWidth={2} />
//               </LineChart>
//             ) : (
//               /* 🔥 SINGLE BAR GRAPH */
//               <BarChart data={buildSingleMetricData(activeCard)}>
//                 {/* <CartesianGrid strokeDasharray="0" vertical={false} /> */}
//                 <XAxis
//                   dataKey="name"
//                   tickFormatter={(label) => {
//                     // If label is a date string like '2025-12-20', show only day part
//                     if (typeof label === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
//                       return label.split('-')[2];
//                     }
//                     return label;
//                   }}
//                 />
//                 <YAxis />
//                 <Tooltip cursor={false} />
//                  {/* ✅ SINGLE HORIZONTAL LINE ON HOVER */}
//            {hoveredBarRect && (
//                                     <g>
//                                         <line
//                                             x1={0}
//                                             x2={hoveredBarRect.x + hoveredBarRect.width}
//                                             y1={hoveredBarRect.y}
//                                             y2={hoveredBarRect.y}
//                                             stroke="#6D5EF7"
//                                             strokeDasharray="4 4"
//                                             strokeWidth={2}
//                                         />
//                                     </g>
//                                 )}
//                 <Bar
//                   dataKey="value"
//                   radius={[6, 6, 0, 0]}
//                   barSize={activeFilter === 'Day' ? 32 : 32}
//                   shape={renderBarShape}
//                 />
//               </BarChart>
//             )}
//           </ResponsiveContainer>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Dashboard;
